// LuviaRx — Admin Compounds tab.
// Full CRUD over the product catalog so future additions don't require a
// code push. Backed by /api/admin/products endpoints in catalog_admin.py.
// UX: expandable list — click a compound to reveal its variants + research
// profile + editors. Add / edit / delete for both compounds and variants.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus, Pencil, Trash2, Loader2, ChevronDown, ChevronRight,
  Eye, EyeOff, X as XIcon,
} from "lucide-react";
import { CATEGORIES } from "../../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

// ---------------------------------------------------------------------------
// Root tab
// ---------------------------------------------------------------------------
export default function CompoundsTab({ token }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSlug, setOpenSlug] = useState(null);
  const [editing, setEditing] = useState(null); // null | {mode:"new"} | {mode:"edit", product}
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load catalog");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const groupedByCategory = useMemo(() => {
    const groups = new Map();
    products.forEach((p) => {
      const key = p.category || "uncategorized";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [products]);

  const deleteCompound = async (slug) => {
    if (!window.confirm(`Delete compound "${slug}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleHidden = async (p) => {
    try {
      await axios.patch(
        `${API}/admin/products/${p.slug}`,
        { hidden: !p.hidden },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to toggle visibility");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-compounds-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="overline text-[#8B2252]">Catalog</p>
          <h2 className="font-display text-2xl text-charcoal-800 mt-1">Compounds</h2>
          <p className="text-sm text-charcoal-500 mt-1 max-w-xl">
            Add, edit, hide, or remove any research compound. Changes are
            live immediately — no redeploy needed.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-compounds-new"
          onClick={() => setEditing({ mode: "new" })}
          className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#8B2252] text-white hover:bg-[#7a1e47]"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
          New compound
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-charcoal-400 text-sm">Loading catalog…</div>
      ) : (
        <div className="space-y-6">
          {groupedByCategory.map(([cat, items]) => {
            const catLabel = CATEGORIES.find((c) => c.id === cat)?.label || cat;
            return (
              <section key={cat} data-testid={`admin-compounds-cat-${cat}`}>
                <p className="overline mb-2">
                  {catLabel} · {items.length}
                </p>
                <div className="space-y-2">
                  {items.map((p) => (
                    <CompoundRow
                      key={p.slug}
                      product={p}
                      open={openSlug === p.slug}
                      token={token}
                      onToggle={() => setOpenSlug(openSlug === p.slug ? null : p.slug)}
                      onEdit={() => setEditing({ mode: "edit", product: p })}
                      onDelete={() => deleteCompound(p.slug)}
                      onToggleHidden={() => toggleHidden(p)}
                      onRefresh={load}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {editing && (
        <CompoundEditor
          token={token}
          product={editing.mode === "edit" ? editing.product : null}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One compound row (expandable to show variants)
// ---------------------------------------------------------------------------
const CompoundRow = ({ product, open, token, onToggle, onEdit, onDelete, onToggleHidden, onRefresh }) => {
  const [variantEditing, setVariantEditing] = useState(null); // null | {mode,variant}
  const priceRange = (() => {
    const prices = (product.variants || []).map((v) => v.price);
    if (!prices.length) return "—";
    const lo = Math.min(...prices); const hi = Math.max(...prices);
    return lo === hi ? usd(lo) : `${usd(lo)}–${usd(hi)}`;
  })();
  const deleteVariant = async (vid) => {
    if (!window.confirm(`Delete variant ${vid}?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${product.slug}/variants/${vid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await onRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };
  return (
    <div
      data-testid={`admin-compound-row-${product.slug}`}
      className={`bg-white rounded-2xl border ${product.hidden ? "border-amber-200 bg-amber-50/40" : "border-charcoal-100"}`}
    >
      <div className="flex items-center gap-3 p-4">
        <button
          type="button"
          onClick={onToggle}
          className="p-1 rounded-full hover:bg-charcoal-100 text-charcoal-500 shrink-0"
          aria-label={open ? "Collapse" : "Expand"}
          data-testid={`admin-compound-toggle-${product.slug}`}
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-charcoal-800 truncate">{product.name}</span>
            <span className="text-[11px] text-charcoal-400 font-mono">{product.slug}</span>
            {product.hidden && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                Hidden
              </span>
            )}
          </div>
          <p className="text-xs text-charcoal-500 mt-0.5 truncate">
            {(product.variants || []).length} variant(s) · {priceRange} · {product.category}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onToggleHidden}
            data-testid={`admin-compound-visibility-${product.slug}`}
            title={product.hidden ? "Un-hide (make visible on shop)" : "Hide from shop"}
            className="p-2 rounded-full hover:bg-charcoal-100 text-charcoal-500"
          >
            {product.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={onEdit}
            data-testid={`admin-compound-edit-${product.slug}`}
            title="Edit compound"
            className="p-2 rounded-full hover:bg-charcoal-100 text-charcoal-500"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`admin-compound-delete-${product.slug}`}
            title="Delete compound"
            className="p-2 rounded-full hover:bg-red-50 text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-charcoal-100 p-4 space-y-4 bg-cream-100/40">
          <div className="flex items-center justify-between">
            <p className="overline">Variants</p>
            <button
              type="button"
              onClick={() => setVariantEditing({ mode: "new" })}
              data-testid={`admin-variant-new-${product.slug}`}
              className="text-xs px-3 py-1.5 rounded-full border border-charcoal-200 hover:bg-cream-200 inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add variant
            </button>
          </div>
          <div className="space-y-2">
            {(product.variants || []).map((v) => (
              <div
                key={v.id}
                data-testid={`admin-variant-${product.slug}-${v.id}`}
                className="flex items-center gap-3 bg-white rounded-xl border border-charcoal-100 p-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-charcoal-800">{v.label}</span>
                    <span className="text-[11px] text-charcoal-400 font-mono">{v.id}</span>
                    {!v.inStock && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-charcoal-100 text-charcoal-500 border border-charcoal-200">
                        Out
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-charcoal-500 mt-0.5">
                    {usd(v.price)} · stock {v.stock}
                    {v.image && ` · ${v.image}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setVariantEditing({ mode: "edit", variant: v })}
                  data-testid={`admin-variant-edit-${product.slug}-${v.id}`}
                  className="p-2 rounded-full hover:bg-charcoal-100 text-charcoal-500"
                  title="Edit variant"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => deleteVariant(v.id)}
                  data-testid={`admin-variant-delete-${product.slug}-${v.id}`}
                  className="p-2 rounded-full hover:bg-red-50 text-red-600"
                  title="Delete variant"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {product.researchProfile && (
            <div className="text-xs bg-white rounded-xl border border-charcoal-100 p-3">
              <p className="overline mb-1">Research profile (Compound Info modal)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-charcoal-600">
                {product.researchProfile.type && <div><b>Type:</b> {product.researchProfile.type}</div>}
                {product.researchProfile.aminoAcids && <div><b>Amino acids:</b> {product.researchProfile.aminoAcids}</div>}
                {product.researchProfile.formula && <div><b>Formula:</b> {product.researchProfile.formula}</div>}
                {product.researchProfile.weight && <div><b>Weight:</b> {product.researchProfile.weight}</div>}
                {product.researchProfile.mechanism && <div className="md:col-span-2"><b>Mechanism:</b> {product.researchProfile.mechanism}</div>}
                {product.researchProfile.pubchem && (
                  <div className="md:col-span-2"><b>PubChem:</b> <a href={product.researchProfile.pubchem} target="_blank" rel="noreferrer" className="text-[#8B2252] underline">{product.researchProfile.pubchem}</a></div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {variantEditing && (
        <VariantEditor
          token={token}
          productSlug={product.slug}
          existingVariant={variantEditing.mode === "edit" ? variantEditing.variant : null}
          onClose={() => setVariantEditing(null)}
          onSaved={async () => { setVariantEditing(null); await onRefresh(); }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Compound editor modal (new OR edit)
// ---------------------------------------------------------------------------
const emptyProfile = {
  type: "", aminoAcids: "", formula: "", weight: "", mechanism: "", pubchem: "",
};

const CompoundEditor = ({ token, product, onClose, onSaved }) => {
  const isEdit = !!product;
  const [form, setForm] = useState(() => ({
    slug: product?.slug || "",
    name: product?.name || "",
    subtitle: product?.subtitle || "",
    category: product?.category || CATEGORIES[0]?.id || "",
    image: product?.image || "",
    imageFallback: product?.imageFallback || "",
    purity: product?.purity || "≥ 99.0%",
    format: product?.format || "Lyophilized",
    coa: product?.coa || "On request",
    testing: product?.testing || "3rd-party",
    description: product?.description || "",
    research_notes: (product?.research_notes || []).join("\n"),
    hidden: product?.hidden || false,
    researchProfile: { ...emptyProfile, ...(product?.researchProfile || {}) },
    variants: product?.variants || [{ id: "10mg", label: "10 mg / vial", price: 0, stock: 20, inStock: true }],
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setProfile = (k, v) =>
    setForm((f) => ({ ...f, researchProfile: { ...f.researchProfile, [k]: v } }));
  const setVariant = (idx, patch) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)) }));
  const addVariant = () =>
    setForm((f) => ({ ...f, variants: [...f.variants, { id: "", label: "", price: 0, stock: 20, inStock: true }] }));
  const removeVariant = (idx) =>
    setForm((f) => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));

  const submit = async () => {
    setError("");
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (!isEdit && !form.slug.trim()) { setError("Slug is required"); return; }
    const notes = form.research_notes.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 8);
    const cleanedProfile = Object.fromEntries(
      Object.entries(form.researchProfile).map(([k, v]) => [k, (v || "").trim() || null]),
    );
    const profileHasAny = Object.values(cleanedProfile).some((v) => v);
    const payload = {
      name: form.name.trim(),
      subtitle: form.subtitle.trim() || null,
      category: form.category,
      image: form.image.trim() || null,
      imageFallback: form.imageFallback.trim() || null,
      purity: form.purity.trim() || null,
      format: form.format.trim() || null,
      coa: form.coa.trim() || null,
      testing: form.testing.trim() || null,
      description: form.description.trim(),
      research_notes: notes,
      hidden: !!form.hidden,
      researchProfile: profileHasAny ? cleanedProfile : null,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/admin/products/${product.slug}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        const createBody = {
          ...payload,
          slug: form.slug.trim(),
          variants: form.variants
            .filter((v) => v.id && v.label)
            .map((v) => ({
              id: v.id.trim(),
              label: v.label.trim(),
              price: Number(v.price) || 0,
              stock: Number(v.stock) || 0,
              inStock: !!v.inStock,
              image: (v.image || "").trim() || null,
              imageFallback: (v.imageFallback || "").trim() || null,
            })),
        };
        await axios.post(`${API}/admin/products`, createBody, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      await onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="compound-editor-modal"
      className="fixed inset-0 z-[60] flex items-start justify-center bg-charcoal-800/60 backdrop-blur-sm overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-cream-100 rounded-3xl shadow-2xl border border-charcoal-100 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-charcoal-100">
          <div>
            <p className="overline text-[#8B2252]">{isEdit ? "Edit compound" : "New compound"}</p>
            <h2 className="font-display text-2xl text-charcoal-800 mt-1">
              {isEdit ? product.name : "Add a new research compound"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="compound-editor-close"
            className="p-2 rounded-full hover:bg-charcoal-100 text-charcoal-500"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basics */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {!isEdit && (
              <div className="md:col-span-2">
                <label className="overline block mb-1">Slug (lowercase, hyphens)</label>
                <input
                  className="input font-mono"
                  data-testid="compound-editor-slug"
                  placeholder="my-new-peptide"
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                />
              </div>
            )}
            <div>
              <label className="overline block mb-1">Name</label>
              <input
                className="input"
                data-testid="compound-editor-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>
            <div>
              <label className="overline block mb-1">Category</label>
              <select
                className="input"
                data-testid="compound-editor-category"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-1">Subtitle</label>
              <input
                className="input"
                value={form.subtitle}
                onChange={(e) => set("subtitle", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-1">Description</label>
              <textarea
                className="input min-h-[90px]"
                data-testid="compound-editor-description"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-1">Research notes (one per line, max 8)</label>
              <textarea
                className="input min-h-[80px]"
                value={form.research_notes}
                onChange={(e) => set("research_notes", e.target.value)}
              />
            </div>
          </section>

          {/* Images + tiles */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Image (path or URL)</label>
              <input
                className="input font-mono text-[13px]"
                placeholder="/products/foo.jpg"
                value={form.image}
                onChange={(e) => set("image", e.target.value)}
              />
            </div>
            <div>
              <label className="overline block mb-1">Image fallback (external URL)</label>
              <input
                className="input font-mono text-[13px]"
                placeholder="https://images.pexels.com/…"
                value={form.imageFallback}
                onChange={(e) => set("imageFallback", e.target.value)}
              />
            </div>
            <div><label className="overline block mb-1">Purity</label><input className="input" value={form.purity} onChange={(e) => set("purity", e.target.value)} /></div>
            <div><label className="overline block mb-1">Format</label><input className="input" value={form.format} onChange={(e) => set("format", e.target.value)} /></div>
            <div><label className="overline block mb-1">COA</label><input className="input" value={form.coa} onChange={(e) => set("coa", e.target.value)} /></div>
            <div><label className="overline block mb-1">Testing</label><input className="input" value={form.testing} onChange={(e) => set("testing", e.target.value)} /></div>
          </section>

          {/* Research profile */}
          <section>
            <p className="overline mb-2">Research profile (shown in Compound Info modal)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input className="input" placeholder="Type (e.g. Synthetic tripeptide)" value={form.researchProfile.type} onChange={(e) => setProfile("type", e.target.value)} />
              <input className="input" placeholder="Amino acids (e.g. 5, N/A)" value={form.researchProfile.aminoAcids} onChange={(e) => setProfile("aminoAcids", e.target.value)} />
              <input className="input font-mono" placeholder="Formula (e.g. C15H26N6O8)" value={form.researchProfile.formula} onChange={(e) => setProfile("formula", e.target.value)} />
              <input className="input" placeholder="Molecular weight (e.g. 418.40 Da)" value={form.researchProfile.weight} onChange={(e) => setProfile("weight", e.target.value)} />
              <textarea className="input md:col-span-2 min-h-[70px]" placeholder="Mechanism / research summary" value={form.researchProfile.mechanism} onChange={(e) => setProfile("mechanism", e.target.value)} />
              <input className="input md:col-span-2 font-mono text-[13px]" placeholder="PubChem URL" value={form.researchProfile.pubchem} onChange={(e) => setProfile("pubchem", e.target.value)} />
            </div>
          </section>

          {/* Variants (only editable on create; use per-variant editor when editing existing compound) */}
          {!isEdit && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <p className="overline">Variants</p>
                <button
                  type="button"
                  onClick={addVariant}
                  className="text-xs px-3 py-1.5 rounded-full border border-charcoal-200 hover:bg-cream-200 inline-flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" /> Add variant
                </button>
              </div>
              <div className="space-y-2">
                {form.variants.map((v, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white p-3 rounded-2xl border border-charcoal-100">
                    <input className="input col-span-2 font-mono" placeholder="10mg" value={v.id} onChange={(e) => setVariant(idx, { id: e.target.value })} />
                    <input className="input col-span-4" placeholder="10 mg / vial" value={v.label} onChange={(e) => setVariant(idx, { label: e.target.value })} />
                    <input className="input col-span-2" type="number" step="0.01" placeholder="Price" value={v.price} onChange={(e) => setVariant(idx, { price: e.target.value })} />
                    <input className="input col-span-2" type="number" placeholder="Stock" value={v.stock} onChange={(e) => setVariant(idx, { stock: e.target.value })} />
                    <label className="col-span-1 text-xs text-charcoal-500 flex items-center gap-1">
                      <input type="checkbox" checked={v.inStock} onChange={(e) => setVariant(idx, { inStock: e.target.checked })} /> In
                    </label>
                    <button type="button" onClick={() => removeVariant(idx)} disabled={form.variants.length === 1} className="col-span-1 p-1 rounded-full hover:bg-red-50 text-red-600 disabled:opacity-30 justify-self-end">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-charcoal-400 mt-2">
                After creating the compound, edit / add / remove sizes from
                the expandable row on the list.
              </p>
            </section>
          )}

          <label className="inline-flex items-center gap-2 text-sm text-charcoal-700 cursor-pointer">
            <input type="checkbox" data-testid="compound-editor-hidden" checked={form.hidden} onChange={(e) => set("hidden", e.target.checked)} />
            Hidden (not shown on the customer shop)
          </label>

          {error && (
            <div className="p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-charcoal-100 bg-cream-200/40 rounded-b-3xl">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm border border-charcoal-200 hover:bg-cream-200">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="compound-editor-submit"
            className="px-5 py-2 rounded-full text-sm font-medium bg-[#8B2252] text-white hover:bg-[#7a1e47] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save changes" : "Create compound"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Variant editor (add or edit) — small dialog
// ---------------------------------------------------------------------------
const VariantEditor = ({ token, productSlug, existingVariant, onClose, onSaved }) => {
  const isEdit = !!existingVariant;
  const [v, setV] = useState({
    id: existingVariant?.id || "",
    label: existingVariant?.label || "",
    price: existingVariant?.price ?? 0,
    stock: existingVariant?.stock ?? 20,
    inStock: existingVariant?.inStock ?? true,
    image: existingVariant?.image || "",
    imageFallback: existingVariant?.imageFallback || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      if (isEdit) {
        await axios.patch(
          `${API}/admin/products/${productSlug}/variants/${existingVariant.id}`,
          {
            label: v.label.trim(),
            price: Number(v.price),
            stock: Number(v.stock),
            inStock: !!v.inStock,
            image: v.image.trim() || null,
            imageFallback: v.imageFallback.trim() || null,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        await axios.post(
          `${API}/admin/products/${productSlug}/variants`,
          {
            id: v.id.trim(),
            label: v.label.trim(),
            price: Number(v.price),
            stock: Number(v.stock),
            inStock: !!v.inStock,
            image: v.image.trim() || null,
            imageFallback: v.imageFallback.trim() || null,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      await onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="variant-editor-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-charcoal-800/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div className="w-full max-w-md bg-cream-100 rounded-3xl shadow-2xl border border-charcoal-100" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-charcoal-100 flex items-start justify-between">
          <h3 className="font-display text-xl text-charcoal-800">{isEdit ? `Edit ${existingVariant.id}` : "New variant"}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-charcoal-100 text-charcoal-500"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          {!isEdit && (
            <input className="input font-mono" data-testid="variant-editor-id" placeholder="Variant id (e.g. 10mg)" value={v.id} onChange={(e) => setV((s) => ({ ...s, id: e.target.value }))} />
          )}
          <input className="input" placeholder="Label (e.g. 10 mg / vial)" value={v.label} onChange={(e) => setV((s) => ({ ...s, label: e.target.value }))} />
          <div className="grid grid-cols-2 gap-2">
            <input className="input" type="number" step="0.01" placeholder="Price" value={v.price} onChange={(e) => setV((s) => ({ ...s, price: e.target.value }))} />
            <input className="input" type="number" placeholder="Stock" value={v.stock} onChange={(e) => setV((s) => ({ ...s, stock: e.target.value }))} />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-charcoal-700">
            <input type="checkbox" checked={v.inStock} onChange={(e) => setV((s) => ({ ...s, inStock: e.target.checked }))} /> In stock
          </label>
          <input className="input font-mono text-[13px]" placeholder="Image path (e.g. /products/foo-10mg.jpg)" value={v.image} onChange={(e) => setV((s) => ({ ...s, image: e.target.value }))} />
          <input className="input font-mono text-[13px]" placeholder="Image fallback URL" value={v.imageFallback} onChange={(e) => setV((s) => ({ ...s, imageFallback: e.target.value }))} />
          {error && <div className="p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-charcoal-100 bg-cream-200/40 rounded-b-3xl">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm border border-charcoal-200 hover:bg-cream-200">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="variant-editor-submit"
            className="px-5 py-2 rounded-full text-sm font-medium bg-[#8B2252] text-white hover:bg-[#7a1e47] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save" : "Add variant"}
          </button>
        </div>
      </div>
    </div>
  );
};
